import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { Box } from '@zillow/constellation-10';

import { useActivateListing } from '../../../../../srcLegacy/shared/hooks/queries/useActivateListing';
import { useScreenSize } from '../../../../hooks';
import { useMultiplexListingFlow } from '../../../../hooks/queries/multiplex';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import { DISPLAY_CONTEXT } from '../../../../../srcLegacy/shared/components/PaymentGateway/constants';
import { PRODUCT_TYPE_NAME_TO_ID } from '../../../../../srcLegacy/shared/constants/FreemiumConstants';
import { EXIT_CTA_LABEL } from '../../../../../srcLegacy/shared/constants/WizardConstants';
import { getDashboardOverviewRoute } from '../../../../constants/urls';

import FreemiumErrorModal from '../../../modals/FreemiumErrorModal';
import { PreActivationModal } from '../../../modals/PreActivationModal';
import PremiumCompare from './components/PremiumCompare';
import PremiumFeatureSummary from './components/PremiumFeatureSummary';

import { trackGtmEvent } from '../../../../analytics';
import { ZRM_GTM_EVENTS } from '../../../../analytics/events/gtm';
import {
  emitActivateListingEvent,
  emitStartActivatePremiumFooterEvent,
} from '../../../../analytics/events/payAndPublish';
import { useAuth } from '../../../AuthProvider';
import PremiumPaymentGateway from '../../../PaymentGateway/PremiumPaymentGateway';
import SpinnerContainer from '../../../SpinnerContainer';
import PageWizard from '../../../Wizard/PageWizard';

import type { FreemiumDatarouterError } from '../../../modals/FreemiumErrorModal';
import type { PageWizardProps } from '../../../Wizard/PageWizard';

type Props = {
  onGoToPrevStep?: (() => void) | null;
  onGoToNextStep?: (() => void) | null;
  onExitWizard?: () => void;
  wizardTitle?: React.ReactNode;
  progressStepper: React.ReactNode;
  totalSteps: number;
  currentStepIndex: number;
  showDesktopViewBreakpoint: PageWizardProps['showDesktopViewBreakpoint'];
};

const PayAndPublish = ({
  onGoToPrevStep,
  onGoToNextStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
}: Props) => {
  const router = useRouter();
  const { user } = useAuth();
  const { isScreenDesktop } = useScreenSize();

  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const { mutate: activate } = useActivateListing();

  const active = listingDetails?.active ?? false;
  const isFreemiumEnabled = listingDetails?.freemium?.isEnabled ?? false;
  const isInBuildingGroup = listingDetails?.freemium?.isInBuildingGroup ?? false;
  const singleFamilyContractDto = listingDetails?.singleFamilyContractDto;

  const [isLoading, setIsLoading] = useState(true);
  const [showPreActivationModal, setShowPreActivationModal] = useState(false);
  const [showPaymentGateway, setShowPaymentGateway] = useState(false);
  const [selectedProductType, setSelectedProductType] = useState(PRODUCT_TYPE_NAME_TO_ID.PREMIUM);
  const [publishError, setPublishError] = useState<{
    error?: FreemiumDatarouterError;
    onClose: () => void;
  }>({ onClose: () => {} });

  const initialActiveRef = useRef(active);
  const premiumCompareRef = useRef<HTMLDivElement>(null);

  const isInReactivationFlow = accessUtils.canAccessMLReactivationFlow({
    lastActivatedMs: listingDetails?.lastActivatedMs,
    isFeed: listingDetails?.isFeed,
    isMultifamily: listingDetails?.isMultifamily,
  });

  const handleWizardComplete = () => {
    if (isInMultiplexListingFlow) {
      onGoToNextStep?.();
      return;
    }
    void router.push(getDashboardOverviewRoute({ listingAlias, showActivationModal: true }));
  };

  const handlePublishClick = (isPremium: boolean) => {
    setSelectedProductType(isPremium ? PRODUCT_TYPE_NAME_TO_ID.PREMIUM : PRODUCT_TYPE_NAME_TO_ID.FREE);

    if (isPremium) {
      setShowPaymentGateway(true);
    } else {
      activate(
        { getParams: { listingAlias } },
        {
          onSuccess: () => {
            const csEventParams = {
              currentProperty: { listingAlias },
              user: user ?? {},
            };

            emitActivateListingEvent(csEventParams);
            trackGtmEvent({ event: ZRM_GTM_EVENTS.RENTAL_MANAGER_PUBLISH });
            handleWizardComplete();
          },
          onError: (error) => {
            setPublishError({
              error: error as FreemiumDatarouterError,
              onClose: () => {
                setPublishError({ error: undefined, onClose: () => {} });
              },
            });
          },
        },
      );
    }
  };

  useEffect(() => {
    if (listingDetails === undefined) return;

    if (active === true) {
      handleWizardComplete();
      return;
    }

    if (singleFamilyContractDto?.isRequired) {
      setShowPreActivationModal(true);
      return;
    }

    if (isFreemiumEnabled === false) {
      handlePublishClick(false);
      return;
    }

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingDetails !== undefined]);

  useEffect(() => {
    // In case activation was triggered elsewhere, complete the wizard
    if (active && !initialActiveRef.current) {
      handleWizardComplete();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const nonFreemiumLabel = singleFamilyContractDto?.isRequired ? 'Next' : 'Continue to payment';
  const nextLabel = isFreemiumEnabled ? 'Go premium' : nonFreemiumLabel;

  const handleNextClick = () => {
    emitStartActivatePremiumFooterEvent({ currentProperty: { listingAlias }, user: user ?? {} });
    handlePublishClick(true);
  };

  return (
    <PageWizard
      hideHeader={false}
      hideFooter={false}
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      nextLabel={nextLabel}
      isNextDisabled={!!singleFamilyContractDto?.isRequired}
      onNextClick={handleNextClick}
      onBackClick={onGoToPrevStep ?? undefined}
      onExitTriggered={onExitWizard ?? undefined}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      exitCTALabel={EXIT_CTA_LABEL.SAVE_AND_EXIT}
      isNarrowLayout={false}
      hideStepIndex={isInReactivationFlow}
      displayPremiumTopOfFunnelHeader={!isInMultiplexListingFlow}
    >
      <Box data-testid="PayAndPublish">
        {isLoading ? (
          <SpinnerContainer />
        ) : (
          <>
            <PremiumFeatureSummary
              onPublishClick={handlePublishClick}
              premiumCompareRef={premiumCompareRef}
              isInBuildingGroup={isInBuildingGroup}
              hideCompareAndLearnMore={!isScreenDesktop}
            />
            <div ref={premiumCompareRef}>
              <PremiumCompare onPublishClick={handlePublishClick} />
            </div>
          </>
        )}
      </Box>

      {publishError.error && (
        <FreemiumErrorModal
          error={publishError?.error}
          onClose={publishError?.onClose}
          onPublishForFreeClick={() => handlePublishClick(false)}
          onTryAgainClick={() => handlePublishClick(true)}
          isPremiumMidfunnelPayment={false}
          selectedProductType={selectedProductType}
        />
      )}

      {showPaymentGateway && listingAlias && (
        <PremiumPaymentGateway
          listingAlias={listingAlias}
          isActive={active}
          freemiumData={listingDetails?.freemium}
          selectedProductType={selectedProductType}
          displayContext={DISPLAY_CONTEXT.MODULAR_LISTING}
          onClose={() => setShowPaymentGateway(false)}
          isInBuildingGroup={isInBuildingGroup}
          onActivateListing={handleWizardComplete}
        />
      )}

      <PreActivationModal
        displayPreActivationModal={showPreActivationModal}
        setDisplayPreActivationModal={setShowPreActivationModal}
        onActivateListing={handleWizardComplete}
      />
    </PageWizard>
  );
};

export default PayAndPublish;
